import { useEffect, useState } from "react";
import { Link, useParams, useSearchParams, useNavigate } from "react-router-dom";
import { Users, Package, Clapperboard, Share2, Settings, BadgeCheck } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/contexts/AuthContext";
import { fmtPrice } from "@/lib/currency";
import { getStore, toggleFollow } from "@/lib/stage";
import { StagePostCard } from "@/components/stage/StagePostCard";
import { StagePostViewer } from "@/components/stage/StagePostViewer";

export default function Store() {
  const { sellerId } = useParams();
  const [params, setParams] = useSearchParams();
  const nav = useNavigate();
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [tab, setTab] = useState("stage");
  const [err, setErr] = useState(null);
  const openPost = params.get("post");

  useEffect(() => { getStore(sellerId).then(setData).catch(() => setErr("This store doesn't exist")); }, [sellerId, user?.id]);

  if (err) return <div className="min-h-[60vh] flex items-center justify-center text-neutral-400">{err}</div>;
  if (!data) return <div className="min-h-[60vh] flex items-center justify-center"><div className="h-10 w-10 rounded-full border-2 border-white/20 border-t-white animate-spin" /></div>;
  const { store, posts, products } = data;
  const shows = posts.filter((p) => p.type === "show");

  const follow = async () => {
    if (!user) return nav(`/register?next=${encodeURIComponent(`/store/${sellerId}`)}`);
    const r = await toggleFollow(sellerId);
    setData((d) => ({ ...d, store: { ...d.store, following: r.following, followers: r.followers } }));
    toast.success(r.following ? `You're in ${store.name}'s audience — you'll hear when they post or go live` : "You left the audience");
  };
  const share = async () => {
    const url = `${window.location.origin}/store/${sellerId}`;
    if (navigator.share) { try { await navigator.share({ title: `${store.name} on VISMARKS`, url }); } catch { /* cancelled */ } return; }
    await navigator.clipboard.writeText(url).catch(() => {});
    toast.success("Store link copied");
  };

  return (
    <div className="min-h-screen bg-[#0A0A0A] text-white" data-testid="store-page">
      <div className="relative h-44 sm:h-64 overflow-hidden">
        {store.cover_url ? <img src={store.cover_url} alt="" className="absolute inset-0 w-full h-full object-cover" /> : <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top_left,#3a1414,transparent_60%),radial-gradient(ellipse_at_bottom_right,#14213a,transparent_60%)]" />}
        <div className="absolute inset-0 bg-gradient-to-t from-[#0A0A0A] via-[#0A0A0A]/40 to-transparent" />
      </div>
      <div className="max-w-5xl mx-auto px-4 -mt-14 relative">
        <div className="flex flex-col sm:flex-row sm:items-end gap-4">
          <div className="h-24 w-24 rounded-2xl border-4 border-[#0A0A0A] bg-[#1c1c1e] overflow-hidden flex items-center justify-center text-3xl font-black shrink-0">
            {store.avatar_url ? <img src={store.avatar_url} alt="" className="w-full h-full object-cover" /> : (store.name || "S")[0]}
          </div>
          <div className="flex-1 min-w-0">
            <h1 className="text-2xl sm:text-3xl font-black tracking-tight flex items-center gap-2" data-testid="store-name">{store.name}{store.founding_seller && <BadgeCheck className="h-5 w-5 text-[#FF9F0A]" title="Founding seller" />}</h1>
            {store.tagline && <div className="text-sm text-neutral-300">{store.tagline}</div>}
            <div className="mt-1 flex flex-wrap gap-4 text-xs text-neutral-400">
              <span className="inline-flex items-center gap-1" data-testid="store-followers"><Users className="h-3.5 w-3.5" />{store.followers} in the audience</span>
              <span className="inline-flex items-center gap-1"><Clapperboard className="h-3.5 w-3.5" />{store.posts} on stage</span>
              <span className="inline-flex items-center gap-1"><Package className="h-3.5 w-3.5" />{store.products} {store.products === 1 ? "product" : "products"}</span>
            </div>
          </div>
          <div className="flex gap-2">
            {store.is_owner
              ? <Button asChild className="rounded-full bg-white text-black hover:bg-neutral-200 font-bold" data-testid="store-manage-btn"><Link to="/seller/stage"><Settings className="h-4 w-4 mr-1" />Manage Stage</Link></Button>
              : <Button onClick={follow} data-testid="store-follow-btn" className={`rounded-full font-bold px-6 ${store.following ? "bg-transparent border border-white/30 text-white hover:bg-white/5" : "bg-[#FF3B30] text-white hover:bg-[#e0352b]"}`}>{store.following ? "In the audience" : "Join the audience"}</Button>}
            <Button variant="outline" onClick={share} data-testid="store-share-btn" className="rounded-full border-white/20 bg-transparent text-white hover:bg-white/5"><Share2 className="h-4 w-4" /></Button>
          </div>
        </div>
        {store.bio && <p className="mt-4 text-sm text-neutral-300 max-w-2xl whitespace-pre-line">{store.bio}</p>}

        <div className="mt-8 flex gap-1 border-b border-white/10">
          {[["stage", `Stage · ${posts.length}`], ["products", `Products · ${products.length}`], ["shows", `Shows · ${shows.length}`]].map(([k, l]) => (
            <button key={k} onClick={() => setTab(k)} data-testid={`store-tab-${k}`} className={`px-4 py-2.5 text-sm font-semibold border-b-2 -mb-px transition-colors ${tab === k ? "border-white text-white" : "border-transparent text-neutral-500 hover:text-white"}`}>{l}</button>
          ))}
        </div>

        <div className="py-6">
          {tab === "stage" && (posts.length ? <Grid>{posts.map((p) => <StagePostCard key={p.id} post={p} compact onOpen={() => setParams({ post: p.id })} />)}</Grid> : <Empty text={store.is_owner ? "Your Stage is empty — post a video, a track, an exhibition or a show." : `${store.name} hasn't posted on their Stage yet. Join the audience to be first to know.`} />)}
          {tab === "shows" && (shows.length ? <Grid>{shows.map((p) => <StagePostCard key={p.id} post={p} compact onOpen={() => setParams({ post: p.id })} />)}</Grid> : <Empty text="No live shows scheduled yet. Join the audience and we'll tell you when the curtain lifts." />)}
          {tab === "products" && (products.length ? (
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3" data-testid="store-products">
              {products.map((p) => (
                <Link key={p.id} to={`/product/${p.id}`} className="rounded-2xl border border-white/10 bg-[#111] overflow-hidden hover:border-white/30 transition-colors">
                  <div className="aspect-square bg-white/5">{p.image_url && <img src={p.image_url} alt="" className="w-full h-full object-cover" loading="lazy" />}</div>
                  <div className="p-2.5"><div className="text-sm font-semibold line-clamp-1">{p.title}</div><div className="text-sm font-mono font-bold text-[#34C759]">{fmtPrice(p.price, p.currency)}</div></div>
                </Link>
              ))}
            </div>
          ) : <Empty text="No products listed yet." />)}
        </div>
      </div>
      <StagePostViewer postId={openPost} onClose={() => { params.delete("post"); setParams(params); }} />
    </div>
  );
}

const Grid = ({ children }) => <div className="grid grid-cols-2 sm:grid-cols-3 gap-3" data-testid="store-stage-grid">{children}</div>;
const Empty = ({ text }) => <div className="rounded-2xl border border-dashed border-white/15 p-10 text-center text-sm text-neutral-500">{text}</div>;
